/**
 * Login screen (ADR-007 overlay, ARCHITECTURE §3): email + password →
 * httpOnly session cookie.
 *
 * Self-contained visual identity (does NOT depend on the vendored theme
 * tokens, which don't all resolve in the web build): a split canvas whose
 * left panel is an ambient knowledge-graph constellation — the product's
 * one true idea, documents resolving into an interlinked wiki — and a
 * disciplined sign-in form on the right. Explicit colors so it renders
 * identically regardless of the app theme.
 */
import { useState, type FormEvent } from "react"
import { Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useAuth } from "../auth-context"

const INK = "#0b1020"
const INK_2 = "#131a33"
const ACCENT = "#ffb454" // warm amber — the single luminous accent
const EDGE = "rgba(255,255,255,0.14)"
const NODE = "rgba(255,255,255,0.55)"

/** Ambient knowledge-graph: fixed layout (deterministic, no RNG). */
function Constellation() {
  const nodes = [
    [18, 22], [42, 14], [70, 26], [86, 48], [30, 44],
    [56, 40], [74, 62], [22, 70], [48, 74], [64, 86], [38, 90], [88, 78],
  ]
  const edges = [
    [0, 1], [1, 2], [2, 3], [0, 4], [4, 5], [5, 2], [5, 6], [3, 6],
    [4, 7], [7, 8], [8, 5], [8, 9], [9, 6], [7, 10], [10, 8], [9, 11], [6, 11],
  ]
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
    >
      <defs>
        <radialGradient id="glow" cx="30%" cy="30%" r="80%">
          <stop offset="0%" stopColor="#26305c" />
          <stop offset="100%" stopColor={INK} />
        </radialGradient>
      </defs>
      <rect width="100" height="100" fill="url(#glow)" />
      {edges.map(([a, b], i) => (
        <line
          key={i}
          x1={nodes[a][0]} y1={nodes[a][1]}
          x2={nodes[b][0]} y2={nodes[b][1]}
          stroke={EDGE} strokeWidth={0.4}
        />
      ))}
      {nodes.map(([x, y], i) => (
        <g key={i}>
          <circle cx={x} cy={y} r={i % 4 === 0 ? 1.7 : 1.1} fill={NODE} />
          {i % 4 === 0 && (
            <circle cx={x} cy={y} r={3.4} fill="none" stroke={ACCENT} strokeWidth={0.3} opacity={0.5}>
              <animate attributeName="r" values="2.4;4.2;2.4" dur={`${3 + (i % 3)}s`} repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.5;0.1;0.5" dur={`${3 + (i % 3)}s`} repeatCount="indefinite" />
            </circle>
          )}
        </g>
      ))}
    </svg>
  )
}

export function LoginScreen() {
  const { t } = useTranslation()
  const { login } = useAuth()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await login(email.trim(), password)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const field: React.CSSProperties = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #d6dae4",
    background: "#fff",
    fontSize: 15,
    color: "#0b1020",
    outline: "none",
  }

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#fff", fontFamily: "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif" }}>
      {/* Left — the idea: documents becoming an interlinked wiki */}
      <div style={{ position: "relative", flex: "1 1 55%", background: INK, color: "#fff", overflow: "hidden", display: "none" }} className="lw-brand">
        <Constellation />
        <div style={{ position: "relative", zIndex: 1, height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "48px 56px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 600, letterSpacing: 0.2 }}>
            <img src="/logo-mark.svg" width={26} height={26} alt="" style={{ borderRadius: 7 }} />
            LLM Wiki
          </div>
          <div style={{ maxWidth: 460 }}>
            <h1 style={{ fontSize: 40, lineHeight: 1.1, fontWeight: 700, letterSpacing: -1, margin: 0 }}>
              {t("login.tagline")}
            </h1>
            <p style={{ marginTop: 16, fontSize: 16, lineHeight: 1.6, color: "rgba(255,255,255,0.66)" }}>
              {t("login.pitch")}
            </p>
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.4)" }}>{t("login.footer")}</div>
        </div>
      </div>

      {/* Right — sign in */}
      <div style={{ flex: "1 1 45%", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ width: "100%", maxWidth: 360 }}>
          <div style={{ marginBottom: 28 }}>
            <div className="lw-brand-sm" style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 20, fontWeight: 600, color: INK }}>
              <img src="/logo-mark.svg" width={24} height={24} alt="" style={{ borderRadius: 6 }} />
              LLM Wiki
            </div>
            <h2 style={{ fontSize: 24, fontWeight: 700, letterSpacing: -0.4, margin: 0, color: INK }}>{t("login.sign_in")}</h2>
            <p style={{ marginTop: 6, fontSize: 14, color: "#5b6478" }}>{t("login.welcome")}</p>
          </div>

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label htmlFor="login-email" style={{ fontSize: 13, fontWeight: 500, color: "#39415a" }}>{t("login.email")}</label>
              <input id="login-email" type="email" autoComplete="email" autoFocus required
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com" style={field}
                onFocus={(e) => (e.target.style.borderColor = ACCENT)}
                onBlur={(e) => (e.target.style.borderColor = "#d6dae4")} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label htmlFor="login-password" style={{ fontSize: 13, fontWeight: 500, color: "#39415a" }}>{t("login.password")}</label>
              <input id="login-password" type="password" autoComplete="current-password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                style={field}
                onFocus={(e) => (e.target.style.borderColor = ACCENT)}
                onBlur={(e) => (e.target.style.borderColor = "#d6dae4")} />
            </div>

            {error && (
              <p role="alert" style={{ fontSize: 13, color: "#c0392b", margin: 0 }}>{error}</p>
            )}

            <button type="submit" disabled={busy || !email.trim() || !password}
              style={{
                marginTop: 4, padding: "11px 16px", borderRadius: 10, border: "none",
                background: busy || !email.trim() || !password ? "#e7e9ef" : INK,
                color: busy || !email.trim() || !password ? "#9aa2b5" : "#fff",
                fontSize: 15, fontWeight: 600, cursor: busy ? "default" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                transition: "background 120ms ease",
              }}>
              {busy && <Loader2 style={{ width: 16, height: 16 }} className="animate-spin" />}
              {t("login.sign_in")}
            </button>
          </form>
        </div>
      </div>

      <style>{`
        @media (min-width: 900px) { .lw-brand { display: block !important; } .lw-brand-sm { display: none !important; } }
      `}</style>
    </div>
  )
}
